import {
  createEncounter,
  addCondition,
  addAllergy,
  addFamilyHistory,
  addProcedure,
  addClinicalImpression,
  addMedicationStatement,
  CreateEncounterData,
  CreateConditionData,
  CreateAllergyData,
  CreateFamilyHistoryData,
  CreateProcedureData,
  CreateClinicalImpressionData,
  CreateMedicationStatementData,
} from '../../api/healthcare';
import { WriteHandler } from './types';

/**
 * Replay handlers for the consultation sub-resource writes queued by
 * ConsultationPage.tsx. Each payload is exactly the object the live
 * (online) code path already builds and passes to the matching
 * src/api/healthcare.ts function -- see ConsultationPage.tsx's
 * handleSave, which is the single source of truth for these shapes.
 *
 * Unlike patient/encounter creation, these six endpoints have no
 * server-side idempotency check (they write straight to FHIR with no
 * local tracking row -- see the Phase 1 investigation notes). A retried
 * replay after a crash mid-batch could in principle create a duplicate.
 * This is accepted for Phase 1: it's the same "please add manually if
 * something didn't save" risk tolerance ConsultationPage already has for
 * its existing partial-failure handling, just extended to a rarer replay
 * scenario instead of a live network failure.
 */

export const createEncounterHandler: WriteHandler = async (row) => {
  const payload = JSON.parse(row.payload) as CreateEncounterData;
  await createEncounter(payload);
};

export const addConditionHandler: WriteHandler = async (row) => {
  if (!row.patient_uuid) throw new Error('add-condition write is missing patient_uuid');
  const payload = JSON.parse(row.payload) as Omit<CreateConditionData, 'patient_record_uuid'>;
  await addCondition(row.patient_uuid, payload);
};

export const addAllergyHandler: WriteHandler = async (row) => {
  if (!row.patient_uuid) throw new Error('add-allergy write is missing patient_uuid');
  const payload = JSON.parse(row.payload) as Omit<CreateAllergyData, 'patient_record_uuid'>;
  await addAllergy(row.patient_uuid, payload);
};

export const addFamilyHistoryHandler: WriteHandler = async (row) => {
  if (!row.patient_uuid) throw new Error('add-family-history write is missing patient_uuid');
  const payload = JSON.parse(row.payload) as Omit<CreateFamilyHistoryData, 'patient_record_uuid'>;
  await addFamilyHistory(row.patient_uuid, payload);
};

export const addProcedureHandler: WriteHandler = async (row) => {
  if (!row.patient_uuid) throw new Error('add-procedure write is missing patient_uuid');
  const payload = JSON.parse(row.payload) as Omit<CreateProcedureData, 'patient_record_uuid'>;
  await addProcedure(row.patient_uuid, payload);
};

export const addClinicalImpressionHandler: WriteHandler = async (row) => {
  if (!row.patient_uuid) throw new Error('add-clinical-impression write is missing patient_uuid');
  const payload = JSON.parse(row.payload) as Omit<
    CreateClinicalImpressionData,
    'patient_record_uuid'
  >;
  await addClinicalImpression(row.patient_uuid, payload);
};

export const addMedicationStatementHandler: WriteHandler = async (row) => {
  if (!row.patient_uuid) throw new Error('add-medication-statement write is missing patient_uuid');
  const payload = JSON.parse(row.payload) as Omit<
    CreateMedicationStatementData,
    'patient_record_uuid'
  >;
  await addMedicationStatement(row.patient_uuid, payload);
};
